import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { ProjectElementDataWithWork } from '../../shared/dto/EntityRepository';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { moveItemsToIndexByKey } from '../../shared/util/moveItemsToIndex';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';
import FloorPlanModalIndexingRow, {
  FLOOR_PLAN_PE_INDEX_DRAG_MIME_TYPE,
  floorPlanPeRowKey,
} from './FloorPlanModalIndexingRow';

const AUTO_SCROLL_SPEED = 7;

export interface FloorPlanModalIndexingProps {
  items: ProjectElementDataWithWork[];
  onReorder: React.Dispatch<React.SetStateAction<ProjectElementDataWithWork[]>>;
  onNext: () => void;
}

const FloorPlanModalIndexing: React.FC<FloorPlanModalIndexingProps> = ({
  items,
  onReorder,
  onNext,
}) => {
  const [reorderPickKeys, setReorderPickKeys] = useState<string[]>([]);

  const [draggingKeys, setDraggingKeys] = useState<string[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragMovingKeysRef = useRef<string[]>([]);

  const workListContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const valid = new Set(items.map((row) => floorPlanPeRowKey(row)));
    setReorderPickKeys((prev) => prev.filter((k) => valid.has(k)));
  }, [items]);

  const toggleReorderPick = useCallback((key: string) => {
    setReorderPickKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  }, []);

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const startAutoScroll = (direction: 'up' | 'down') => {
    stopAutoScroll();

    const scroll = () => {
      if (!workListContainerRef.current) return;

      const scrollAmount = direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED;
      workListContainerRef.current.scrollTop += scrollAmount;

      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    };

    autoScrollIntervalRef.current = requestAnimationFrame(scroll);
  };

  const handleContentDragOver = (e: React.DragEvent) => {
    if (dragMovingKeysRef.current.length === 0 || !workListContainerRef.current) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const container = workListContainerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50;

    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      startAutoScroll('up');
    } else if (
      distanceFromBottom < scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight
    ) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  };

  const handleContentDragLeave = () => {
    stopAutoScroll();
  };

  useEffect(() => {
    if (dragMovingKeysRef.current.length === 0 && draggingKeys.length === 0) {
      stopAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, [draggingKeys]);

  const resetAfterDrop = (movedKeys: string[]) => {
    dragMovingKeysRef.current = [];
    setDraggingKeys([]);
    setDragOverIndex(null);
    stopAutoScroll();
    if (movedKeys.length > 1) {
      setReorderPickKeys([]);
    }
  };

  const handleDragRowEnd = () => {
    dragMovingKeysRef.current = [];
    setDraggingKeys([]);
    setDragOverIndex(null);
    stopAutoScroll();
  };

  const beginReorderDrag = useCallback(
    (item: ProjectElementDataWithWork) => {
      const lead = floorPlanPeRowKey(item);
      const orderedSel = items
        .filter((row) => reorderPickKeys.includes(floorPlanPeRowKey(row)))
        .map((row) => floorPlanPeRowKey(row));
      const block =
        orderedSel.length > 1 && reorderPickKeys.includes(lead) ? orderedSel : [lead];
      dragMovingKeysRef.current = block;
      setDraggingKeys(block);
    },
    [items, reorderPickKeys]
  );

  const handleDropSpaceDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDropSpaceDragLeave = () => {
    setDragOverIndex(null);
  };

  /** 행이 아닌 공백(힌트·드롭 슬롯·빈 영역) 클릭 시 다중 선택 해제 — WorkListPdfModalSelect 의 `data-suppress-marquee` 격리 패턴과 동일하게 data 속성으로 행만 구분 */
  const handleWorkListClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-floor-plan-index-row]')) return;
    setReorderPickKeys([]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const fromTransfer = e.dataTransfer.getData(FLOOR_PLAN_PE_INDEX_DRAG_MIME_TYPE);
      const keys =
        dragMovingKeysRef.current.length > 0
          ? [...dragMovingKeysRef.current]
          : fromTransfer
            ? [String(fromTransfer)]
            : [];

      if (keys.length === 0) return;
      onReorder((prev) => moveItemsToIndexByKey(prev, floorPlanPeRowKey, keys, dropIndex));
      resetAfterDrop(keys);
    },
    [onReorder]
  );

  const n = items.length;

  return (
    <>
      <WorkListContainer
        ref={workListContainerRef}
        onClickCapture={handleWorkListClickCapture}
        onDragOver={handleContentDragOver}
        onDragLeave={handleContentDragLeave}
      >
        <WorkIndexingDescription>
          <EachDescriptionLine>
            {getPageMessage('WORK_INDEXING_HINT_DRAG_REORDER')}
          </EachDescriptionLine>
        </WorkIndexingDescription>
        {n === 0 ? (
          <NoContentContainer>
            {getModalMessage('WORK_LIST_PDF_INDEXING_PLACEHOLDER')}
          </NoContentContainer>
        ) : (
          <>
            {items.map((item, index) => (
              <React.Fragment key={floorPlanPeRowKey(item)}>
                <DropSpace
                  onDrop={(e) => handleDrop(e, index)}
                  onDragOver={(e) => handleDropSpaceDragOver(e, index)}
                  onDragLeave={handleDropSpaceDragLeave}
                  $isActive={dragOverIndex === index}
                />
                <FloorPlanModalIndexingRow
                  item={item}
                  orderIndex={index}
                  isDragging={draggingKeys.includes(floorPlanPeRowKey(item))}
                  isReorderPicked={reorderPickKeys.includes(floorPlanPeRowKey(item))}
                  onToggleReorderPick={() => toggleReorderPick(floorPlanPeRowKey(item))}
                  onDragStart={() => beginReorderDrag(item)}
                  onDragEnd={handleDragRowEnd}
                />
              </React.Fragment>
            ))}
            <DropSpace
              onDrop={(e) => handleDrop(e, n)}
              onDragOver={(e) => handleDropSpaceDragOver(e, n)}
              onDragLeave={handleDropSpaceDragLeave}
              $isActive={dragOverIndex === n}
              $fillRemaining
            />
          </>
        )}
      </WorkListContainer>
      <ButtonContainer>
        <HeadlessBtn
          value={getBtnName('NEXT_STEP')}
          handleClick={onNext}
          StyledBtn={PaperBlurFitBtn}
        />
      </ButtonContainer>
    </>
  );
};


const WorkListContainer = styled.div`
  width: 100%;
  height: 75vh;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  white-space: pre-line;
`;

const WorkIndexingDescription = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
`;

const EachDescriptionLine = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const DropSpace = styled.div<{ $isActive?: boolean; $fillRemaining?: boolean }>`
  width: 100%;
  ${({ $fillRemaining }) => ($fillRemaining ? 'flex: 1;' : '')}
  ${({ $fillRemaining, $isActive }) =>
    !$fillRemaining
      ? `
    height: ${$isActive ? '60px' : '8px'};
    min-height: ${$isActive ? '30px' : '8px'};
  `
      : `
    min-height: ${$isActive ? '60px' : '20px'};
  `}
  margin: 2px 0;
  border-radius: 2px;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `1px dashed ${theme.colors.color_Gray_04}` : `0.5px dashed transparent`};
  transition: all 0.4s ease-out;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default FloorPlanModalIndexing;
